// Body of the tempurpedic OrderIssuePage FORM state: header_content Toolbox
// stream, the order-issue section (rich-text intro, error banner, the
// order-issue form -- no <h1> and no info aside, unlike the contact page), and
// the footer_content Toolbox stream. The `.l-capped-width` wrapper carries the
// extra `order-issue-container` class (a live contact-us.scss anchor:
// `&.order-issue-container { margin-top: 0 }`). The two Toolbox streams render
// through the shared toolbox page map with tagName={React.Fragment} (no
// per-block Wagtail wrapper; ContactFormPageBody precedent).
//
// The form is rendered natively in React from the reactivated-serialized
// OrderIssueFormRenderable; the captcha is the standalone
// <ReactivatedReCaptchaField> (excluded from serialization; no `recaptcha/api.js`
// script tag is needed here -- CaptchaLibrary injects it client-side). The
// field widgets carry any `?prefill=` initial values the server decrypted (the
// encrypted deep-link prefill). The error banner arrives pre-localized as a
// string prop (server-side gettext) so fr_CA is preserved; the submit label
// "Submit" is a client-side ttag string.
import { Immutable } from "immer";
import React from "react";
import { FormErrorList } from "tsi-common-react/src/forms/FormErrorList/index";
import { ReactivatedFormWidget } from "tsi-common-react/src/forms/ReactivatedFormWidget/index";
import {
    ReactivatedReCaptchaField,
    useReCaptchaSubmit,
} from "tsi-common-react/src/forms/ReactivatedReCaptcha/index";
import {
    FORM_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";
import { t } from "ttag";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { buildToolboxPageMap } from "../../blocks/toolboxPageMap";
import { useContactFormEnhancements } from "../useContactFormEnhancements";

type IProps = Immutable<templates.OrderIssuePageTemplate>;

const REQUIRED_FIELD_CLASS_NAME = concatClassNames([
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
]);

const OrderIssueForm = (
    props: Pick<
        IProps,
        "form" | "recaptcha_action" | "error_banner_text" | "captcha_errors"
    >,
): React.JSX.Element => {
    const { form } = props;
    const onSubmit = useReCaptchaSubmit(props.recaptcha_action);
    const { formRef, messageLabel, onReasonsChange } =
        useContactFormEnhancements(form);
    // The captcha's errors ride a side-channel (the field is excluded from
    // the serialized form), so a captcha-only failure must still raise the
    // banner.
    const hasErrors = Boolean(form.errors) || props.captcha_errors.length > 0;
    return (
        <>
            {hasErrors && (
                <div className="contact-form__errors-title">
                    {props.error_banner_text}
                </div>
            )}
            {/* The `js-kuid-form` class is a live analytics contract
                (kuidListeners binds submit listeners on it) and `novalidate`
                defers validation to the server. */}
            <form
                action=""
                method="post"
                className={concatClassNames([
                    FORM_CLASS_NAME,
                    "form--contact",
                    "js-kuid-form",
                ])}
                noValidate
                ref={formRef}
                onSubmit={onSubmit}
                onChange={onReasonsChange}
            >
                <CSRFToken />
                {form.iterator.map((name) => {
                    const field = form.fields[name];
                    const errors = form.errors?.[name];
                    const errorId = `${name.toLowerCase()}-errors`;
                    const fieldHasErrors = Boolean(errors && errors.length > 0);
                    const ariaDescribedBy = [
                        "recipients",
                        "name",
                        "email",
                        "message",
                    ].includes(name)
                        ? errorId
                        : undefined;
                    const autoComplete =
                        name === "name"
                            ? "name"
                            : name === "email"
                              ? "email"
                              : undefined;
                    return (
                        <div
                            key={name}
                            className={
                                field.widget.required
                                    ? REQUIRED_FIELD_CLASS_NAME
                                    : FORM_FIELD_CLASS_NAME
                            }
                        >
                            <label htmlFor={field.widget.attrs.id}>
                                {name === "message" && messageLabel !== null
                                    ? messageLabel
                                    : field.label}
                            </label>
                            <ReactivatedFormWidget
                                widget={field.widget}
                                ariaDescribedBy={ariaDescribedBy}
                                ariaInvalid={fieldHasErrors}
                                autoComplete={autoComplete}
                            />
                            {errors && errors.length > 0 && (
                                <FormErrorList id={errorId} errors={errors} />
                            )}
                        </div>
                    );
                })}
                {/* This must be the last form <div>: .form--contact's
                    `> div:last-of-type` centering + label clipping key on
                    that position. */}
                <ReactivatedReCaptchaField
                    fieldClassName={REQUIRED_FIELD_CLASS_NAME}
                    errors={props.captcha_errors}
                />
                <span>
                    <input className="button" type="submit" value={t`Submit`} />
                </span>
            </form>
        </>
    );
};

export const OrderIssuePageBody = (props: IProps): React.JSX.Element => {
    const toolboxMap = buildToolboxPageMap({
        hardcoded_ssr: props.hardcoded_ssr,
        hotspot_ssr: props.hotspot_ssr,
        breadcrumbs_ssr: props.breadcrumbs_ssr,
        customer_service_ssr: props.customer_service_ssr,
        shop_compare_ssr: props.shop_compare_ssr,
        tab_feature_attribute_ssr: props.tab_feature_attribute_ssr,
        reviews_ssr: props.reviews_ssr,
    });
    return (
        <div className="l-capped-width order-issue-container">
            <StreamField
                tagName={React.Fragment}
                value={props.page.header_content.value}
                components={toolboxMap}
            />

            <div className="contact-us-container">
                <section>
                    <div className="intro-text">
                        <div
                            className="rich-text"
                            dangerouslySetInnerHTML={{
                                __html: props.intro_html,
                            }}
                        />
                    </div>

                    <OrderIssueForm
                        form={props.form}
                        recaptcha_action={props.recaptcha_action}
                        error_banner_text={props.error_banner_text}
                        captcha_errors={props.captcha_errors}
                    />
                </section>
            </div>

            <StreamField
                tagName={React.Fragment}
                value={props.page.footer_content.value}
                components={toolboxMap}
            />
        </div>
    );
};
